import React, { useState, useEffect, useContext, useRef } from 'react';
import { AuthContext } from '../context/AuthContext';
import axios from 'axios';
import { Link } from 'react-router-dom';
import '../styles/PointsGuide.css';

const PointsGuide = () => {
  const { isAuthenticated } = useContext(AuthContext);
  const [guideData, setGuideData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [activeCategory, setActiveCategory] = useState('all');
  const hasFetched = useRef(false);

  useEffect(() => {
    if (!hasFetched.current) {
      hasFetched.current = true;
      fetchPointsGuide();
    }
    
    // Listen for daily check-in completion to refresh stats
    const handleCheckInComplete = () => {
      console.log('Daily check-in completed, refreshing points guide...');
      setTimeout(() => fetchPointsGuide(false), 1000); // Silent refresh
    };
    
    window.addEventListener('dailyCheckInComplete', handleCheckInComplete);
    
    return () => {
      window.removeEventListener('dailyCheckInComplete', handleCheckInComplete);
    };
  }, []);

  const fetchPointsGuide = async (showLoading = true) => {
    try {
      if (showLoading) setLoading(true);
      setError('');
      
      const token = localStorage.getItem('token');
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      
      const response = await axios.get('/api/points-guide', { headers });
      setGuideData(response.data.data);
    } catch (err) {
      console.error('Error fetching points guide:', err);
      setError('Failed to load points guide');
    } finally {
      if (showLoading) setLoading(false);
    }
  };

  const getCategoryOpportunities = () => {
    if (!guideData) return [];
    
    if (activeCategory === 'all') {
      return guideData.opportunities;
    }
    
    return guideData.categories[activeCategory] || [];
  };

  const getCategoryColor = (category) => {
    const colors = {
      all: '#6366f1',
      daily: '#f59e0b',
      engagement: '#10b981',
      content: '#8b5cf6',
      social: '#ec4899',
      achievements: '#f97316'
    };
    return colors[category] || '#6366f1';
  };

  const getCategoryIcon = (category) => {
    const icons = {
      all: '🌟',
      daily: '📅',
      engagement: '💬',
      content: '✨',
      social: '👥',
      achievements: '🏆'
    };
    return icons[category] || '🌟';
  };

  if (loading) {
    return (
      <div className="points-guide-container">
        <div className="loading-container">
          <div className="spinner"></div>
          <p>Loading Points Guide...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="points-guide-container">
        <div className="error-message">
          <p>{error}</p>
          <button onClick={fetchPointsGuide} className="retry-button">
            Retry
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="points-guide-container">
      {/* Header */}
      <div className="points-guide-header">
        <div className="header-content">
          <h1 className="page-title">
            <span className="title-icon">💰</span>
            Points Earning Guide
          </h1>
          <p className="page-subtitle">
            Discover all the ways to earn points and level up your GamersUnion experience!
          </p>
        </div>
      </div>

      {/* User Stats Dashboard (if authenticated) */}
      {isAuthenticated && guideData?.userStats && (
        <div className="user-stats-dashboard">
          <div className="stats-grid">
            <div className="stat-card total-points">
              <div className="stat-icon">🎯</div>
              <div className="stat-content">
                <div className="stat-value">{guideData.userStats.totalPoints.toLocaleString()}</div>
                <div className="stat-label">Total Points Earned</div>
              </div>
            </div>
            
            <div className="stat-card available-points">
              <div className="stat-icon">💎</div>
              <div className="stat-content">
                <div className="stat-value">{guideData.userStats.availablePoints.toLocaleString()}</div>
                <div className="stat-label">Available Points</div>
              </div>
            </div>
            
            <div className="stat-card today-earned">
              <div className="stat-icon">⭐</div>
              <div className="stat-content">
                <div className="stat-value">+{guideData.userStats.todayEarned.toLocaleString()}</div>
                <div className="stat-label">Earned Today</div>
              </div>
            </div>
            
            <div className="stat-card streak">
              <div className="stat-icon">🔥</div>
              <div className="stat-content">
                <div className="stat-value">{guideData.userStats.currentStreak}</div>
                <div className="stat-label">Day Streak</div>
              </div>
            </div>
          </div>

          <div className="quick-actions">
            <Link to="/shop" className="action-button primary">
              <span>🛍️</span> Spend Points
            </Link>
            <Link to="/achievements" className="action-button secondary">
              <span>🏆</span> View Achievements
            </Link>
            <Link to="/leaderboard" className="action-button secondary">
              <span>📊</span> Leaderboard
            </Link>
          </div>
        </div>
      )}

      {/* Not Logged In Message */}
      {!isAuthenticated && (
        <div className="login-prompt">
          <div className="prompt-icon">🎮</div>
          <h3>Start Earning Points Today!</h3>
          <p>Create an account to track your points, complete daily quests, and unlock achievements.</p>
          <div className="prompt-actions">
            <Link to="/register" className="btn-primary">Create Account</Link>
            <Link to="/login" className="btn-secondary">Log In</Link>
          </div>
        </div>
      )}

      {/* Category Filters */}
      <div className="category-filters">
        {['all', 'daily', 'engagement', 'content', 'social', 'achievements'].map(category => (
          <button
            key={category}
            className={`category-button ${activeCategory === category ? 'active' : ''}`}
            onClick={() => setActiveCategory(category)}
            style={{
              '--category-color': getCategoryColor(category)
            }}
          >
            <span className="category-icon">{getCategoryIcon(category)}</span>
            <span className="category-name">
              {category.charAt(0).toUpperCase() + category.slice(1)}
            </span>
          </button>
        ))}
      </div>

      {/* Opportunities Grid */}
      <div className="opportunities-section">
        <div className="section-header">
          <h2>
            {activeCategory === 'all' ? 'All Ways to Earn' : `${activeCategory.charAt(0).toUpperCase() + activeCategory.slice(1)} Activities`}
          </h2>
          <p className="opportunity-count">
            {getCategoryOpportunities().length} {getCategoryOpportunities().length === 1 ? 'way' : 'ways'} to earn
          </p>
        </div>

        <div className="opportunities-grid">
          {getCategoryOpportunities().map((opportunity, index) => (
            <div key={index} className="opportunity-card">
              <div className="opportunity-icon">{opportunity.icon}</div>
              <div className="opportunity-content">
                <h3 className="opportunity-title">{opportunity.action}</h3>
                <p className="opportunity-description">{opportunity.description}</p>
              </div>
              <div className="opportunity-points">
                <span className="points-badge">{opportunity.points}</span>
                <span className="points-label">points</span>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Tips Section */}
      <div className="tips-section">
        <h2>💡 Pro Tips for Maximizing Points</h2>
        <div className="tips-grid">
          <div className="tip-card">
            <div className="tip-icon">🔥</div>
            <h3>Build Your Streak</h3>
            <p>Check in daily to maintain your streak and earn bonus points. The longer your streak, the bigger the rewards!</p>
          </div>
          
          <div className="tip-card">
            <div className="tip-icon">📋</div>
            <h3>Complete Daily Quests</h3>
            <p>Five new quests appear every day. Complete all of them to maximize your daily point earnings.</p>
          </div>
          
          <div className="tip-card">
            <div className="tip-icon">🏆</div>
            <h3>Hunt Achievements</h3>
            <p>Achievements award substantial point bonuses. Check your progress regularly and aim for the next unlock.</p>
          </div>
          
          <div className="tip-card">
            <div className="tip-icon">🎯</div>
            <h3>Stay Active</h3>
            <p>The most active community members earn the most points. Engage regularly for consistent rewards.</p>
          </div>
        </div>
      </div>

      {/* FAQ Section */}
      <div className="faq-section">
        <h2>❓ Frequently Asked Questions</h2>
        <div className="faq-list">
          <div className="faq-item">
            <h3>What can I do with points?</h3>
            <p>Points can be spent in the <Link to="/shop">Shop</Link> to unlock exclusive profile themes and customizations. They also determine your rank on the <Link to="/leaderboard">Leaderboard</Link>.</p>
          </div>
          
          <div className="faq-item">
            <h3>Do points expire?</h3>
            <p>No! Your total points never decrease. However, spent points are deducted from your available balance.</p>
          </div>
          
          <div className="faq-item">
            <h3>What happens if I break my streak?</h3>
            <p>Your streak resets to 1 on your next check-in, but you keep all points earned. You can always start a new streak!</p>
          </div>
          
          <div className="faq-item">
            <h3>How are daily quests determined?</h3>
            <p>Everyone gets the same 5 daily quests. They reset at midnight (server time) and provide fresh opportunities to earn points.</p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PointsGuide;
